<script setup>
import { onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useForm } from 'vee-validate';
import InputText from 'primevue/inputtext';
import Password from 'primevue/password';
import Button from 'primevue/button';
import * as yup from '@/helpers/yup-distilled';

import { router, getNextRouteFromNewSigninState } from '@/helpers';
import { useAuthStore } from '@/stores';
import { useUserPreferenceStore } from '@/stores';
import { getFriendlyErrorMessage } from '@/helpers';

const schema = yup.object().shape({
	username: yup.string().required('Username is required'),
	password: yup.string().required('Password is required')
});

const { defineField, errors, isSubmitting, handleSubmit } = useForm({
	validationSchema: schema
});

const onSubmit = handleSubmit((values, { setErrors }) => {
	const { username, password } = values;

	return authStore
		.login(username, password)
		.then(async () => {
			await userPreferenceStore.getAllUserPreferences();
			// redirect to previous url or default to home page
			router.push(
				getNextRouteFromNewSigninState(authStore.signInState, route)
			);
		})
		.catch(async (error) => {
			const errorOutput = await getFriendlyErrorMessage(error);
			setErrors({ apiError: errorOutput.message });
		});
});

const onRequestResetPassword = async () => {
	if (username.value) {
		authStore.setLoginForResetPasswordByPasscode(username.value);
		await authStore.requestPasscodeForPasswordReset().then(async () => {
			router.push(
				getNextRouteFromNewSigninState(authStore.signInState, route)
			);
		});
	} else {
		router.push(
			`${import.meta.env.VITE_SIGNIN_BASEURL}resetpasswordbypasscode/specifylogin`
		);
	}
};

// CLASSIC ASPIRE SHIM
// attempt to auto refresh token before startup
onMounted(async () => {
	try {
		if (await authStore.refreshToken()) {
			location.replace(location.pathname);
		}
	} catch {
		// just eat it in this case
	}
});
//END CLASSIC ASPIRE SHIM

const route = useRoute();
const authStore = useAuthStore();
const userPreferenceStore = useUserPreferenceStore();

// Top of the flow - clear any stored reset-by-passcode config
authStore.clearLoginForResetPasswordByPasscode();

const [username] = defineField('username');
const [password] = defineField('password');
</script>

<template>
	<form @submit="onSubmit">
		<div class="p-fluid login-small-form">
			<div class="p-field mt-2">
				<label class="font-semibold" for="username">Username</label>
				<InputText
					autocomplete="username"
					autofocus
					v-model="username"
					type="text"
					:invalid="!!errors.username"
				/>
				<div class="p-error">{{ errors.username }}&nbsp;</div>
			</div>
			<div>
				<label class="font-semibold" for="password">Password</label>
				<Password
					autocomplete="current-password"
					v-model="password"
					toggle-mask
					:feedback="false"
					:invalid="!!errors.password"
				/>
				<div class="p-error">{{ errors.password }}&nbsp;</div>
			</div>
			<div class="mb-2 flex flex-row justify-content-center">
				<span @click="onRequestResetPassword" class="link-span"
					>Forgotten your password, or need to set a new one?</span
				>
			</div>
		</div>
		<div class="p-fluid">
			<Button
				:disabled="isSubmitting"
				label="Submit"
				type="submit"
			></Button>
			<div v-if="errors.apiError" class="p-error">
				{{ errors.apiError }}
			</div>
		</div>
	</form>
</template>
<style scoped>
.link-span {
	cursor: pointer;
	text-decoration: underline;
	color: var(--text-color-secondary);
}
</style>
